import { listWorkerStatuses, workerWorkState, type WorkerWorkState } from "@/api";
import {
  DataTableSource,
  type DataTablePageReq,
  type DataTablePageResp,
  type DataTableSourceOptions,
} from "@carbon6/c6-frontend-design";
import {
  createWorkerStatusTableRows,
  WorkerStatusTableRow,
  type WorkerStatusRowClickHandler,
} from "./WorkerStatusTableRow";

// 보존 윈도우(12h) 안에 보고한 워커 목록은 페이지네이션이 없다(워커 수는 적다). 한 번에 모두
// 받아 작업 상태·이름순으로 정렬한 단일 페이지로 표시한다. 페이지는 주기적으로 refresh()를
// 호출해 최신 status·current_job·나이를 갱신한다. "Live only" 토글이 켜지면 offline(임계
// 나이 초과) 워커를 빼고 보여준다.
export class WorkerStatusTableSource extends DataTableSource<WorkerStatusTableRow> {
  observedAt: string | null = null;
  // 켜지면 online 워커만 표시한다. 기본은 12h 전체다.
  private liveOnly = false;
  private request: Promise<Awaited<ReturnType<typeof listWorkerStatuses>>> | null = null;
  // 직전 fetch의 online 워커 수(필터 적용 전 기준). 요약 캡션이 "전체 중 N live"를 보여줄 때 쓴다.
  private liveCount = 0;
  // 행 클릭 핸들러. fetch마다 새로 만드는 행에 그대로 전달한다. 그래서 폴링 뒤에도 클릭이 동작한다.
  private readonly rowClickHandler: WorkerStatusRowClickHandler | null;

  constructor(
    rowClickHandler: WorkerStatusRowClickHandler | null = null,
    // initialLiveOnly: URL 에서 읽은 첫 조건. 첫 로드에 바로 적용한다. 그래서 initialize 뒤
    // setter 가 no-op 이다.
    {
      initialLiveOnly = false,
      ...options
    }: DataTableSourceOptions & { initialLiveOnly?: boolean } = {},
  ) {
    super(options);
    this.rowClickHandler = rowClickHandler;
    this.liveOnly = initialLiveOnly;
  }

  // worker_id로 현재 스냅샷의 행을 찾는다. 상세는 행 인스턴스를 보관하지 않고, 열려 있는 동안
  // 이 조회로 다시 읽는다. 폴링이 행을 새로 만든다. 인스턴스를 보관하면 값이 그 시점에 멈춘다.
  findRow(workerId: string): WorkerStatusTableRow | null {
    return this.getSnapshot().rows.find((row) => row.getRowId() === workerId) ?? null;
  }

  setLiveOnly(value: boolean): Promise<void> {
    if (value === this.liveOnly) {
      return Promise.resolve();
    }
    this.liveOnly = value;
    return this.goToPage(1);
  }

  // 직전 fetch에서 online 으로 판정된 워커 수(Live only 토글 여부와 관계없음).
  getLiveCount(): number {
    return this.liveCount;
  }

  protected override async onPage(
    req: DataTablePageReq,
  ): Promise<DataTablePageResp<WorkerStatusTableRow>> {
    this.request ??= listWorkerStatuses().finally(() => {
      this.request = null;
    });
    const response = await this.request;
    this.observedAt = response.observed_at;
    const workers = response.workers;
    const sorted = sortByWorkStateThenName(
      createWorkerStatusTableRows(workers, this.rowClickHandler),
    );
    // 필터 적용 전 online 수를 먼저 집계한다. 그래서 토글이 켜져도 요약은 전체 기준 live 수를 보여준다.
    this.liveCount = sorted.filter((row) => row.online).length;
    const rows = this.liveOnly ? sorted.filter((row) => row.online) : sorted;
    return { rows, total: rows.length, page: 1, size: Math.max(req.size, rows.length) };
  }
}

// 작업 중 → 대기 → 오프라인 순으로 묶고, 묶음 안에서는 worker_id 사전순이다.
//
// **보고 시각을 정렬에 쓰지 않는다.** 워커마다 heartbeat 시점이 다르다. 그래서 폴링(5초)마다
// 상대 순서가 바뀌고, 운영자가 보고 있는 행이 계속 위치를 바꾼다. 작업 상태와 worker_id 는
// 거의 변하지 않는다. 그래서 목록 순서가 유지된다.
//
// 오프라인 워커는 작업 상태와 섞지 않고 맨 아래에 둔다. 그 워커의 current_job 은 "마지막으로
// 처리하던 job" 이기 때문이다. 작업 중으로 분류하면 죽은 워커가 일하는 것처럼 보인다(목록
// 셀·상세가 경과를 멈추는 것과 같은 규약).
const WORK_STATE_ORDER: Record<WorkerWorkState, number> = { working: 0, idle: 1, offline: 2 };

function sortByWorkStateThenName(rows: WorkerStatusTableRow[]): WorkerStatusTableRow[] {
  return [...rows].sort((a, b) => {
    const byWorkState = WORK_STATE_ORDER[workerWorkState(a)] - WORK_STATE_ORDER[workerWorkState(b)];
    if (byWorkState !== 0) {
      return byWorkState;
    }
    // worker_id 는 k8s pod name 이라 끝에 숫자가 붙는다. numeric 비교를 쓰면 worker-2 가
    // worker-10 앞에 온다.
    return a.worker_id.localeCompare(b.worker_id, undefined, { numeric: true });
  });
}
